// Helper modules (moved to top)
import { parseGPX } from '../utils/gpxParser';
import { adaptGpxDataToPoints } from './gpx_adapter';
import { resampleByDistance } from './resampler';
import { smoothElevations } from './smoother';
import { reconstructGpx } from './gpx_reconstructor';
import { GpxPoint, LatLonPoint } from './types';
import { ElevationProvider } from './elevation_provider';
import { GPXData } from '../types'; // Import the original GPXData type

// Core types and interfaces
export * from './types';
export * from './elevation_provider';

// Implementations
export * from './providers/opentopodata'; // Export the provider class
// Add exports for other providers (Google, OpenElevation) when implemented

// ---- Export this interface ---- 
export interface AugmentationOptions {
  resampleIntervalMeters?: number; // e.g., 50
  smoothingWindow?: number; // e.g., 5 (0 or 1 to disable)
  // Add other options like output format later if needed
}

/**
 * Main orchestration function to augment GPX elevation.
 * 
 * @param gpxString - The raw GPX file content as a string.
 * @param elevationProvider - An instance of a class implementing ElevationProvider.
 * @param options - Configuration options for resampling and smoothing.
 * @returns The reconstructed GPX string with augmented elevation data.
 */
export async function augmentGpxElevation(
  gpxString: string,
  elevationProvider: ElevationProvider,
  options: AugmentationOptions = {}
): Promise<string> {
  console.log('Starting GPX elevation augmentation...');

  // 1. Parse the original GPX file
  const originalGpxData: GPXData = parseGPX(gpxString);
  if (!originalGpxData || !originalGpxData.coordinates || originalGpxData.coordinates.length === 0) {
      throw new Error('Failed to parse GPX or GPX file contains no track points.');
  }
  console.log(`Parsed ${originalGpxData.coordinates.length} original points.`);

  // Extract original metadata to preserve it
  const originalMetadata = originalGpxData.metadata;

  // 2. Adapt to our GpxPoint structure
  const initialPoints: GpxPoint[] = adaptGpxDataToPoints(originalGpxData);

  // 3. Resample points by distance
  const resampleInterval = options.resampleIntervalMeters;
  let pointsToQuery: GpxPoint[];
  if (resampleInterval && resampleInterval > 0) {
      pointsToQuery = resampleByDistance(initialPoints, resampleInterval);
      console.log(`Resampled to ${pointsToQuery.length} points at approx ${resampleInterval}m intervals.`);
  } else {
      pointsToQuery = initialPoints; // Use original points if no resampling
      console.log('Skipping resampling, using original points for elevation lookup.');
  }

  // Prepare points for elevation API (LatLon only)
  const latLonPoints: LatLonPoint[] = pointsToQuery.map(p => ({ lat: p.lat, lon: p.lon }));

  // 4. Fetch elevations from the selected provider
  const fetchedElevations = await elevationProvider.getElevations(latLonPoints);

  if (fetchedElevations.length !== pointsToQuery.length) {
    console.warn(`Elevation results length (${fetchedElevations.length}) does not match requested points length (${pointsToQuery.length}). There might be missing data.`);
    // Handle mismatch - e.g., pad with 0 or last known elevation? For now, proceed cautiously.
  }

  // 5. Merge fetched elevations back into points
  // We create a new array to hold the points with new elevations
  let pointsWithNewElevation: GpxPoint[] = pointsToQuery.map((point, index) => ({
      ...point,
      ele: fetchedElevations[index] ?? point.ele ?? 0 // Use fetched, fallback to original (if any), then 0
  }));

  // 6. Smooth elevations (optional)
  const smoothingWindow = options.smoothingWindow;
  if (smoothingWindow && smoothingWindow > 1) {
      const elevationsToSmooth = pointsWithNewElevation.map(p => p.ele as number); // Assume elevation is now always number
      const smoothedElevations = smoothElevations(elevationsToSmooth, smoothingWindow);
      
      // Update points with smoothed elevations
      pointsWithNewElevation = pointsWithNewElevation.map((point, index) => ({
          ...point,
          ele: smoothedElevations[index]
      }));
      console.log(`Applied smoothing with window size ${smoothingWindow}.`);
  } else {
      console.log('Skipping elevation smoothing.');
  }

  // 7. Reconstruct the GPX file
  console.log('Reconstructing GPX file...');
  const finalGpxString = reconstructGpx(pointsWithNewElevation, {
      name: originalMetadata.name || 'Augmented Track'
      // Pass other metadata if needed
  });

  console.log('GPX elevation augmentation complete.');
  return finalGpxString;
}

// Example usage (can be removed or placed in a separate test file):
/*
import { OpenTopodataProvider } from './providers/opentopodata';

async function runExample() {
    const dummyGpx = `<?xml version="1.0"?>
<gpx version="1.1" creator="dummy">
  <metadata><name>Test Track</name></metadata>
  <trk><name>Test Track</name><trkseg>
    <trkpt lat="40.7128" lon="-74.0060"><ele>10</ele></trkpt>
    <trkpt lat="40.7138" lon="-74.0050"><ele>12</ele></trkpt>
    <trkpt lat="40.7148" lon="-74.0040"><ele>11</ele></trkpt>
    <trkpt lat="40.7158" lon="-74.0030"><ele>15</ele></trkpt>
  </trkseg></trk>
</gpx>`;

    const provider = new OpenTopodataProvider();
    const options: AugmentationOptions = {
        resampleIntervalMeters: 50,
        smoothingWindow: 3
    };

    try {
        const augmentedGpx = await augmentGpxElevation(dummyGpx, provider, options);
        console.log('\nAugmented GPX:\n', augmentedGpx);
    } catch (error) {
        console.error('Example failed:', error);
    }
}

// runExample();
*/ 